import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ManagementToolSidebarService } from '../management-tool-sidebar.service';
import {
  FAKE_EMAIL_DOMAINS,
  FAKE_FIRST_NAMES,
  FAKE_LAST_NAMES,
  FAKE_STREET_NAMES,
  FAKE_SUBURBS,
} from './fake-data';

export type ObfuscationType =
  | 'name'
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'id'
  | 'dob'
  | 'address';

/**
 * Deterministic PII obfuscation service.
 * Uses DJB2 hash to map real values to fake values consistently —
 * the same input always produces the same output across sessions,
 * browsers, and page refreshes.
 */
@Injectable({ providedIn: 'root' })
export class ObfuscationService {
  private _sidebar = inject(ManagementToolSidebarService);

  isActive$: Observable<boolean> = this._sidebar.obfuscateNames$;

  get isActiveSnapshot(): boolean {
    return this._sidebar.obfuscateNames$.value;
  }

  obfuscateName(realName: string): string {
    if (!realName) {
      return realName;
    }
    const parts = realName.trim().split(/\s+/);
    if (parts.length >= 2) {
      const firstName = this._pickFromList(parts[0], FAKE_FIRST_NAMES);
      const lastName = this._pickFromList(
        parts[parts.length - 1],
        FAKE_LAST_NAMES
      );
      return `${firstName} ${lastName}`;
    }
    return this._pickFromList(realName, FAKE_FIRST_NAMES);
  }

  obfuscateFirstName(realName: string): string {
    if (!realName) {
      return realName;
    }
    return this._pickFromList(realName.trim(), FAKE_FIRST_NAMES);
  }

  obfuscateLastName(realName: string): string {
    if (!realName) {
      return realName;
    }
    return this._pickFromList(realName.trim(), FAKE_LAST_NAMES);
  }

  obfuscateEmail(realEmail: string): string {
    if (!realEmail) {
      return realEmail;
    }
    const localPart = realEmail.split('@')[0] ?? realEmail;
    const fakeFirst = this._pickFromList(
      localPart,
      FAKE_FIRST_NAMES
    ).toLowerCase();
    const fakeLast = this._pickFromList(
      realEmail,
      FAKE_LAST_NAMES
    ).toLowerCase();
    const fakeDomain = this._pickFromList(realEmail, FAKE_EMAIL_DOMAINS);
    return `${fakeFirst}.${fakeLast}@${fakeDomain}`;
  }

  obfuscatePhone(realPhone: string): string {
    if (!realPhone) {
      return realPhone;
    }

    const prefix = realPhone.startsWith('+64') ? '+64 21 ' : '021 ';
    const digitCount = 7;
    let digits = '';
    for (let index = 0; index < digitCount; index++) {
      const positionHash = this._djb2Hash(`${realPhone}:${index}`);
      digits += (positionHash % 10).toString();
    }
    return `${prefix}${digits.slice(0, 3)} ${digits.slice(3)}`;
  }

  obfuscateId(realId: string): string {
    if (!realId) {
      return realId;
    }
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let index = 0; index < realId.length; index++) {
      const positionHash = this._djb2Hash(`${realId}:${index}`);
      const charIndex = positionHash % chars.length;
      result += chars[charIndex];
    }
    return result;
  }

  obfuscateDob(realDob: string): string {
    if (!realDob) {
      return realDob;
    }
    const hash = this._djb2Hash(realDob);
    const dayOffset = (hash % 28) + 1;
    const monthOffset = (hash % 12) + 1;
    const yearBase = 1950 + (hash % 50);
    const day = dayOffset.toString().padStart(2, '0');
    const month = monthOffset.toString().padStart(2, '0');
    return `${day}/${month}/${yearBase}`;
  }

  obfuscateAddress(realAddress: string): string {
    if (!realAddress) {
      return realAddress;
    }
    const hash = this._djb2Hash(realAddress);
    const streetNumber = (hash % 200) + 1;
    const street = this._pickFromList(realAddress, FAKE_STREET_NAMES);
    const suburb = this._pickFromList(realAddress + '_suburb', FAKE_SUBURBS);
    return `${streetNumber} ${street}, ${suburb}`;
  }

  /**
   * Convenience method: returns real or obfuscated value based on current toggle state.
   */
  transform(value: string, type: ObfuscationType): string {
    if (!this.isActiveSnapshot) {
      return value;
    }
    return this._obfuscateByType(value, type);
  }

  /**
   * Observable version of transform — emits when toggle state changes.
   */
  transform$(value: string, type: ObfuscationType): Observable<string> {
    return this.isActive$.pipe(
      map((isActive) => (isActive ? this._obfuscateByType(value, type) : value))
    );
  }

  obfuscateByType(value: string, type: ObfuscationType): string {
    return this._obfuscateByType(value, type);
  }

  private _obfuscateByType(value: string, type: ObfuscationType): string {
    switch (type) {
      case 'name':
        return this.obfuscateName(value);
      case 'firstName':
        return this.obfuscateFirstName(value);
      case 'lastName':
        return this.obfuscateLastName(value);
      case 'email':
        return this.obfuscateEmail(value);
      case 'phone':
        return this.obfuscatePhone(value);
      case 'id':
        return this.obfuscateId(value);
      case 'dob':
        return this.obfuscateDob(value);
      case 'address':
        return this.obfuscateAddress(value);
      default:
        return this.obfuscateName(value);
    }
  }

  /**
   * DJB2 hash function — deterministic, fast, and well-distributed.
   * Same input always produces the same output (no randomness).
   */
  private _djb2Hash(input: string): number {
    let hash = 5381;
    for (let index = 0; index < input.length; index++) {
      // eslint-disable-next-line no-bitwise
      hash = (hash * 33) ^ input.charCodeAt(index);
    }
    return Math.abs(hash);
  }

  private _pickFromList(input: string, list: string[]): string {
    const hash = this._djb2Hash(input);
    return list[hash % list.length];
  }
}
